import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Link } from "react-router-dom";
import { ArrowRight, Leaf, ShieldCheck, Clock, Eye, BadgeCheck, Check } from "lucide-react";
import { CtaButtons, CtaBand } from "@/components/Cta";
import { Reveal, stagger, item } from "@/components/Reveal";
import { WHY_CHOOSE, STEPS, IMAGES, RESIDENTIAL, COMMERCIAL_CATEGORIES } from "@/data/site";
import { ServiceAreaMap } from "@/components/ServiceAreaMap";
import { RecurringPromise, OfferBanner } from "@/components/RecurringPromise";
import { usePricing } from "@/context/PricingProvider";
import { useSEO, webPageSchema } from "@/lib/seo";
import { PAGE_META } from "@/data/page_meta";


const HERO_LINES = ["Healthy Spaces.", "Smarter Solutions.", "Reliable Service."];
const WHY_ICONS = [Leaf, Clock, Eye, ShieldCheck, BadgeCheck];
const COMMERCIAL_PREVIEW = COMMERCIAL_CATEGORIES.map((c) => c.category);

export default function Home() {
  const pricing = usePricing();
  useSEO({ ...PAGE_META.home, jsonLd: webPageSchema({ path: "/", name: PAGE_META.home.title, description: PAGE_META.home.description }) });
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-center pt-28 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(95,165,68,0.10),transparent_45%),radial-gradient(circle_at_85%_15%,rgba(242,169,184,0.12),transparent_40%)]" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10 items-center relative z-10 w-full">
          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-leaf/10 text-leaf mb-7 border border-leaf/20">
              <Leaf className="w-4 h-4" />
              <span className="text-xs font-medium tracking-wide uppercase">Serving All of DC, Maryland &amp; Northern Virginia</span>
            </motion.div>

            <h1 className="font-heading font-bold tracking-tighter leading-[0.92] text-navy text-5xl sm:text-6xl md:text-7xl">
              {HERO_LINES.map((line, i) => (
                <span key={line} className="block overflow-hidden">
                  <motion.span className="block"
                    initial={{ y: "110%" }} animate={{ y: 0 }}
                    transition={{ duration: 0.9, delay: 0.15 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}>
                    {i === 2 ? <span className="text-leaf">{line}</span> : line}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7, duration: 0.7 }}
              className="mt-7 text-xl md:text-2xl text-muted-foreground max-w-xl leading-relaxed font-light">
              Healthier spaces start with a better clean.
            </motion.p>

            <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.6 }}>
              <CtaButtons className="mt-9" />
            </motion.div>

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1, duration: 0.6 }}
              className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-navy/70">
              {["Green Cleaning included", "Background-checked pros", "The CJB Promise"].map((t) => (
                <span key={t} className="flex items-center gap-2"><Check className="w-4 h-4 text-leaf" />{t}</span>
              ))}
            </motion.div>
          </div>

          <div className="lg:col-span-5">
            <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.4, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="relative">
              <div className="relative rounded-[2rem] rounded-tr-md overflow-hidden aspect-[4/5] shadow-[0_30px_80px_rgba(27,42,91,0.25)]">
                <motion.img style={{ y: imgY, scale: imgScale }} src={IMAGES.hero} alt="Clean, healthy living space" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/30 to-transparent" />
              </div>
              <motion.div animate={{ y: [0, -10, 0] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-5 border border-border max-w-[200px]">
                <p className="font-heading text-3xl font-bold text-navy">100%</p>
                <p className="text-sm text-muted-foreground">Green Cleaning — no upcharge, ever.</p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="py-6 bg-navy border-y border-white/10" data-testid="home-marquee">
        <Marquee speed={40} gradient={false} autoFill>
          {["Green Cleaning Included", "Health First", "Transparent Pricing", "Background-Checked Professionals", "The CJB Promise", "Serving DC · Maryland · Northern Virginia"].map((t) => (
            <span key={t} className="mx-12 font-heading text-2xl md:text-3xl font-medium text-white/85 flex items-center gap-12">
              {t} <Leaf className="w-5 h-5 text-leaf" />
            </span>
          ))}
        </Marquee>
      </div>

      {/* WHY CHOOSE - BENTO */}
      <section className="py-24 md:py-32" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="max-w-2xl mb-14">
            <p className="text-xs uppercase tracking-[0.25em] text-leaf mb-4">Why Choose CJB</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight text-navy leading-none">
              Cleaning that puts your <span className="text-leaf">health first.</span>
            </h2>
          </Reveal>

          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
            className="grid grid-cols-1 md:grid-cols-6 gap-5 auto-rows-fr">
            {WHY_CHOOSE.map((w, i) => {
              const Icon = WHY_ICONS[i];
              const span = i === 0 ? "md:col-span-3 md:row-span-2" : i === 1 ? "md:col-span-3" : "md:col-span-2";
              const featured = i === 0;
              return (
                <motion.div key={w.title} variants={item}
                  data-testid={`why-card-${i}`}
                  className={`${span} group relative rounded-3xl p-8 border transition-all duration-300 hover:-translate-y-1 ${featured ? "bg-navy text-white border-navy" : "bg-white border-border hover:border-leaf/40"}`}>
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-6 ${featured ? "bg-leaf" : "bg-leaf/10"}`}>
                    <Icon className={`w-6 h-6 ${featured ? "text-white" : "text-leaf"}`} />
                  </div>
                  <span className={`inline-block text-[11px] uppercase tracking-widest mb-3 px-3 py-1 rounded-full ${featured ? "bg-white/10 text-white/80" : "bg-muted text-muted-foreground"}`}>{w.tag}</span>
                  <h3 className={`font-heading font-bold tracking-tight mb-2 ${featured ? "text-2xl md:text-3xl" : "text-2xl"}`}>{w.title}</h3>
                  <p className={`leading-relaxed ${featured ? "text-white/70 text-lg" : "text-muted-foreground"}`}>{w.desc}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* GREEN CLEANING */}
      <section className="py-24 md:py-32 bg-muted/50" data-testid="green-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="relative rounded-[2rem] rounded-bl-md overflow-hidden aspect-[5/4] shadow-2xl">
              <img src={IMAGES.green} alt="A healthy, green living space" className="w-full h-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-xs uppercase tracking-[0.25em] text-leaf mb-4">Green Cleaning</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight text-navy leading-none mb-6">
              Included with every service. Not an upgrade.
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-6">
              Green Cleaning is part of our standard process — never an optional add-on or premium tier.
              We clean without compromising the health of the people who live and work in your space.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-8">
              When commercial facilities require specific disinfectants, products, equipment, or protocols
              to meet contractual or operational requirements, we're happy to accommodate those specifications.
            </p>
            <Link to="/services" className="group inline-flex items-center gap-2 font-medium text-navy hover:text-leaf transition-colors" data-testid="green-services-link">
              Explore our services <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="py-24 md:py-32" data-testid="services-preview">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="max-w-2xl mb-14">
            <p className="text-xs uppercase tracking-[0.25em] text-leaf mb-4">What We Clean</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight text-navy leading-none">
              Residential &amp; Commercial.
            </h2>
          </Reveal>
          <div className="grid lg:grid-cols-2 gap-6">
            <Reveal>
              <Link to="/services" className="relative block rounded-3xl overflow-hidden h-full min-h-[380px] group" data-testid="preview-residential">
                <img src={IMAGES.residential} alt="Residential cleaning" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/40 to-transparent" />
                <div className="absolute bottom-0 p-8 text-white">
                  <h3 className="font-heading text-3xl font-semibold mb-3">Residential</h3>
                  <p className="text-white/80 mb-4">Standard, Deep, Move-In/Move-Out &amp; Recurring plans for a healthier home.</p>
                  <div className="flex flex-wrap gap-2">
                    {RESIDENTIAL.map((s) => <span key={s.slug} className="text-xs px-3 py-1 rounded-full bg-white/15 backdrop-blur">{s.name}</span>)}
                  </div>
                </div>
              </Link>
            </Reveal>
            <Reveal delay={0.1}>
              <Link to="/services/commercial-cleaning" className="relative block rounded-3xl overflow-hidden h-full min-h-[380px] group" data-testid="preview-commercial">
                <img src={IMAGES.commercial} alt="Commercial cleaning" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/40 to-transparent" />
                <div className="absolute bottom-0 p-8 text-white">
                  <h3 className="font-heading text-3xl font-semibold mb-3">Commercial</h3>
                  <p className="text-white/80 mb-4">Offices, property management, community facilities &amp; retail across the DMV.</p>
                  <div className="flex flex-wrap gap-2">
                    {COMMERCIAL_PREVIEW.map((s) => <span key={s} className="text-xs px-3 py-1 rounded-full bg-white/15 backdrop-blur">{s}</span>)}
                  </div>
                </div>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* RECURRING PROMISE */}
      <RecurringPromise variant="section" promise={pricing.recurring_promise} />

      {/* WHAT TO EXPECT */}
      <section className="py-24 md:py-32 bg-navy text-white overflow-hidden" data-testid="what-to-expect">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="max-w-2xl mb-16">
            <p className="text-xs uppercase tracking-[0.25em] text-leaf mb-4">What to Expect</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight leading-none">
              Six simple steps to a healthier space.
            </h2>
          </Reveal>
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
            {STEPS.map((s) => (
              <motion.div key={s.n} variants={item} className="border-t border-white/15 pt-6" data-testid={`step-${s.n}`}>
                <p className="font-heading text-5xl font-bold text-leaf/80 mb-4">{s.n}</p>
                <h3 className="font-heading text-xl font-semibold mb-2">{s.title}</h3>
                <p className="text-white/60 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* SERVICE AREA MAP */}
      <section className="py-24 md:py-32" data-testid="home-service-area">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="max-w-2xl mb-12">
            <p className="text-xs uppercase tracking-[0.25em] text-leaf mb-4">Where We Clean</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight text-navy leading-none">
              Serving all around the DMV.
            </h2>
            <p className="mt-5 text-lg text-muted-foreground">
              From our Alexandria office we care for homes and businesses across Arlington, McLean, Tysons,
              Falls Church, Fairfax, Washington DC, and the surrounding communities.
            </p>
            <p className="mt-4 text-base text-navy flex items-center gap-2" data-testid="home-hours">
              <Clock className="w-4 h-4 text-leaf" /> Hours: Mon to Fri 7 am to 7 pm, Sat 10 am to 5 pm, Sun closed
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <ServiceAreaMap />
          </Reveal>
        </div>
      </section>

      <CtaBand />
    </div>
  );
}
